import React, { useState, useEffect } from "react";
import { PORTFOLIO as PD } from "./data";
import { useI18n, useTheme } from "./hooks";
import { CustomCursor } from "./CustomCursor";
import { HeroBg, RegionMap, HeroH1, SchematicAnimated } from "./Hero";
import { HeroPhoto } from "./Portrait";
import {
  Manifesto, WorksSection, CaseFile, SkillsSection,
  TimelineSection, VoicesSection, WritingSection, ContactSection, Foot,
} from "./Sections";
import "./portfolio.css";

const SECTION_IDS = ["top", "works", "skills", "record", "writing", "contact"];

// Tracks which section is under the upper third of the viewport.
function useActiveSection() {
  const [active, setActive] = useState("top");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const els = SECTION_IDS.slice(1).map((id) => document.getElementById(id)).filter(Boolean);
    const visible = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) visible.add(e.target.id); else visible.delete(e.target.id); });
      const current = SECTION_IDS.filter((id) => visible.has(id)).pop();
      setActive(current || "top");
    }, { rootMargin: "-35% 0px -60% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return active;
}

function Nav({ lang, setLang, theme, toggleTheme, t }) {
  const activeId = useActiveSection();
  const items = [
    ["#top", t(PD.i18n.nav.index)],
    ["#works", t(PD.i18n.nav.works)],
    ["#skills", t(PD.i18n.nav.practice)],
    ["#record", t(PD.i18n.nav.record)],
    ["#writing", t(PD.i18n.nav.writing)],
    ["#contact", t(PD.i18n.nav.contact)],
  ];
  return (
    <header className="d3-nav" role="banner">
      <div className="d3-nav-left">
        <span className="d3-bracket">[</span>
        <b>HI3RIS</b>
        <span className="d3-mute"> / mitm.portfolio</span>
        <span className="d3-bracket">]</span>
      </div>
      <nav className="d3-nav-mid" aria-label="Primary">
        {items.map(([h, l]) => {
          const on = h.slice(1) === activeId;
          return <a key={h} href={h} className={on ? "is-on" : undefined} aria-current={on ? "location" : undefined}>{l}</a>;
        })}
      </nav>
      <div className="d3-nav-right">
        <span className="d3-pill-live" aria-hidden="true"><span className="d3-pulse" /> LIVE</span>
        <div className="lang-switch" role="group" aria-label="Language">
          <button type="button" className={lang === "fr" ? "is-on" : ""} onClick={() => setLang("fr")} aria-pressed={lang === "fr"}>FR</button>
          <button type="button" className={lang === "en" ? "is-on" : ""} onClick={() => setLang("en")} aria-pressed={lang === "en"}>EN</button>
        </div>
        <button type="button" className="theme-btn" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}>
          {theme === "dark" ? "☼" : "☾"}
        </button>
      </div>
    </header>
  );
}

function ScrollLine() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? Math.min(1, window.scrollY / h) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="scroll-line" aria-hidden="true"><div className="scroll-line-bar" style={{ width: (p * 100) + "%" }} /></div>;
}

export default function PortfolioApp() {
  const { lang, setLang, t } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const [active, setActive] = useState(null);
  const [ghProjects, setGhProjects] = useState(null);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    fetch("https://api.github.com/users/hi3ris/repos?per_page=100&sort=updated", {
      signal: ac.signal,
      cache: "no-cache",
    })
      .then(r => r.ok ? r.json() : [])
      .then((repos) => {
        if (!Array.isArray(repos)) return;
        const byName = {};
        repos.forEach((r) => { byName[r.name.toLowerCase()] = r; });
        const enriched = PD.projects.map((p) => {
          const m = byName[p.id.toLowerCase()] || byName[p.name.toLowerCase().replace(/\s+/g, "-")];
          if (m) return { ...p, _gh: { stars: m.stargazers_count, url: m.html_url, lang: m.language } };
          return p;
        });
        setGhProjects(enriched);
      })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetch("https://dev.to/api/articles?username=hi3ris&per_page=8", {
      signal: ac.signal,
      cache: "no-cache",
    })
      .then(r => r.ok ? r.json() : [])
      .then((arts) => {
        if (!Array.isArray(arts) || arts.length === 0) return;
        setArticles(arts.map((a) => ({
          date: (a.published_at || "").slice(0, 7),
          title: { fr: a.title, en: a.title },
          read: (a.reading_time_minutes || 5) + " min",
          url: a.url,
        })));
      })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  return (
    <div id="top" className="d3-root">
      <CustomCursor />
      <ScrollLine />
      <svg className="d3-crosshair" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <pattern id="d3grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--c-grid)" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#d3grid)" />
      </svg>

      <Nav lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme} t={t} />

      <main id="main">
        <section className="d3-hero" aria-label="Intro">
          <HeroBg />
          <div className="hero-foreground">
            <div className="d3-hero-cap">
              <span>{t(PD.i18n.hero_cap)}</span>
              <span>{PD.i18n.hero_loc}</span>
            </div>

            <HeroH1 t={t} />

            <div className="d3-hero-side" role="complementary" aria-label="Subject summary">
              <div className="d3-side-block">
                <div className="d3-side-h">{t(PD.i18n.subject_h)}</div>
                <div>{t(PD.i18n.subject_b)}</div>
              </div>
              <div className="d3-side-block">
                <div className="d3-side-h">{t(PD.i18n.vector_h)}</div>
                <div>{t(PD.i18n.vector_b)}</div>
              </div>
              <div className="d3-side-block">
                <div className="d3-side-h">{t(PD.i18n.payload_h)}</div>
                <div>{t(PD.i18n.payload_b)}</div>
              </div>
            </div>

            <SchematicAnimated t={t} />

            <div className="hero-bottom">
              <RegionMap />
              <div className="d3-hero-stats">
                {PD.stats.map((s, i) => (
                  <div key={i} className="d3-hstat">
                    <div className="d3-hstat-i">0{i+1}</div>
                    <div className="d3-hstat-v">{s.value}</div>
                    <div className="d3-hstat-l">{t(s.label)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <HeroPhoto />
        </section>

        <Manifesto t={t} />
        <WorksSection t={t} lang={lang} onOpen={setActive} projects={ghProjects} />
        <SkillsSection t={t} />
        <TimelineSection t={t} />
        <VoicesSection t={t} />
        <WritingSection t={t} articles={articles} />
        <ContactSection t={t} />
        <Foot t={t} />
      </main>

      <CaseFile project={active} onClose={() => setActive(null)} t={t} lang={lang} />
    </div>
  );
}
